import * as React from 'react';
import {
  TextInput,
  SelectInput,
  ImageInput,
  ImageField,
  required,
  ReferenceInput,
  ReferenceArrayInput,
  SelectArrayInput,
  useTranslate,
  useLocaleState,
} from 'react-admin';
import { RichTextInput } from 'ra-input-rich-text';
import { Box, Typography } from '@mui/material';

import InputRow from '../form/InputRow';
import FormSection from '../form/FormSection';
import SelectUserByEmailInput from '../form/SelectUserByEmailInput';
import SourceLanguageBanner from '../form/SourceLanguageBanner';
import { useSourceLanguage } from '../../hooks/useSourceLanguage';

const FieldHint = ({ children, tone = 'error' }) => (
  <Typography
    component="span"
    sx={{
      display: 'inline-block',
      fontSize: 12,
      mb: 1,
      color: tone === 'info' ? '#fff' : 'error.main',
      bgcolor: tone === 'info' ? '#0ABAB5' : 'transparent',
      borderRadius: tone === 'info' ? '8px' : 0,
      px: tone === 'info' ? 1 : 0,
      py: tone === 'info' ? 0.5 : 0,
    }}
  >
    {children}
  </Typography>
);

/**
 * Mekan create/edit — web VenueApplicationForm ile aynı bölüm / 2’li grid.
 */
export function VenueFormFields({ isEdit = false, record = null, permissions = null }) {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const localeNameKey = locale === 'tr' ? 'name.tr' : 'name.en';
  const { localizedSource } = useSourceLanguage({ isEdit, record });

  return (
    <>
      <SourceLanguageBanner isEdit={isEdit} record={record} />

      <FormSection title={translate('venues.basic_info', { _: 'Temel Bilgiler' })}>
        <InputRow>
          <TextInput
            source="name"
            label={translate('venues.name_short', { _: 'Ad' })}
            fullWidth
            variant="outlined"
            validate={required()}
          />
          <TextInput
            source="slug"
            label="Slug"
            fullWidth
            variant="outlined"
            validate={required()}
          />
        </InputRow>
        <ReferenceArrayInput source="categories" reference="venueCategories">
          <SelectArrayInput
            optionText={(choice) => {
              if (!choice) return '';
              const n = choice.name;
              if (n && typeof n === 'object') {
                return n[locale] || n.tr || n.en || choice.id;
              }
              if (typeof n === 'string' && n.trim()) return n;
              return choice.id;
            }}
            label={translate('venues.categories_short', { _: 'Kategori' })}
            fullWidth
            variant="outlined"
            validate={required()}
          />
        </ReferenceArrayInput>
      </FormSection>

      <FormSection title={translate('venues.media', { _: 'Medya' })}>
        <InputRow>
          <Box>
            <FieldHint>
              {translate('venues.square_img_hint', {
                _: 'Kare oranlı görsel yüklenmelidir.',
              })}
            </FieldHint>
            <ImageInput source="logo" label="Logo" accept="image/*" defaultValue={null}>
              <ImageField source="src" />
            </ImageInput>
          </Box>
          <Box>
            <FieldHint>
              {translate('venues.landscape_img_hint', {
                _: 'Yatay oranlı görsel yüklenmelidir.',
              })}
            </FieldHint>
            <ImageInput source="banner" label="Banner" accept="image/*" defaultValue={null}>
              <ImageField source="src" />
            </ImageInput>
          </Box>
        </InputRow>
      </FormSection>

      <FormSection title={translate('venues.venue_layout', { _: 'Mekan Planı' })}>
        <FieldHint>
          {translate('venues.square_img_hint', {
            _: 'Kare oranlı görsel yüklenmelidir.',
          })}
        </FieldHint>
        <ImageInput
          source="layout"
          label={translate('venues.venue_layout', { _: 'Mekan Planı' })}
          accept="image/*"
          defaultValue={null}
        >
          <ImageField source="src" />
        </ImageInput>
      </FormSection>

      <FormSection title="Youtube">
        <FieldHint tone="info">
          {translate('venues.youtube_help', {
            _: 'Lütfen sadece video ID’sini giriniz.',
          })}
        </FieldHint>
        <TextInput
          source="youtube"
          label="Youtube ID"
          fullWidth
          variant="outlined"
          defaultValue={null}
        />
      </FormSection>

      <FormSection title={translate('venues.contact', { _: 'İletişim' })}>
        <InputRow>
          <TextInput
            source="phone"
            label={translate('venues.phone_short', { _: 'Tel' })}
            fullWidth
            variant="outlined"
            defaultValue={null}
          />
          <TextInput
            source="email"
            label="E-posta"
            fullWidth
            variant="outlined"
            defaultValue={null}
          />
        </InputRow>
      </FormSection>

      <FormSection title={translate('venues.about', { _: 'Mekan Hakkında' })}>
        <RichTextInput
          source={localizedSource('about')}
          multiline
          rows={5}
          label={translate('venues.about_short', { _: 'Hakkında' })}
          fullWidth
          validate={required()}
        />
      </FormSection>

      <FormSection title={translate('venues.address_info', { _: 'Adres' })}>
        <InputRow>
          <ReferenceInput source="city" reference="cities">
            <SelectInput
              optionText={localeNameKey}
              label={translate('venues.city', { _: 'Şehir' })}
              fullWidth
              variant="outlined"
              validate={required()}
            />
          </ReferenceInput>
          <TextInput
            source="address"
            label={translate('venues.address_short', { _: 'Adres' })}
            fullWidth
            variant="outlined"
            validate={required()}
          />
        </InputRow>
        <TextInput
          source="coordinates"
          label={translate('venues.coords_short', { _: 'Koordinat' })}
          fullWidth
          variant="outlined"
          defaultValue={null}
        />
      </FormSection>

      <FormSection title={translate('venues.legal', { _: 'Yasal' })}>
        <InputRow columns={3}>
          <SelectInput
            source="company.type"
            label={translate('venues.type_short', { _: 'Tip' })}
            choices={[
              { id: 'K', name: 'KK' },
              { id: 'M', name: 'MŞ' },
              { id: 'Y', name: 'YŞ' },
              { id: 'R', name: 'RK' },
            ]}
            fullWidth
            variant="outlined"
            validate={required()}
          />
          <TextInput
            source="company.name"
            label={translate('venues.entity_short', { _: 'Tüzel' })}
            fullWidth
            variant="outlined"
            validate={required()}
          />
          <TextInput
            source="company.taxName"
            label={translate('venues.tax_name_short', { _: 'Vergi adı' })}
            fullWidth
            variant="outlined"
            validate={required()}
          />
        </InputRow>
        <ImageInput
          source="taxDocument"
          label={translate('venues.tax_doc_short', { _: 'Vergi belgesi' })}
          accept="image/*"
          defaultValue={null}
        >
          <ImageField source="src" />
        </ImageInput>
      </FormSection>

      <FormSection title={translate('venues.financial', { _: 'Finansal' })}>
        <InputRow columns={3}>
          <TextInput
            source="bank.companyName"
            label={translate('venues.account_short', { _: 'Hesap adı' })}
            fullWidth
            variant="outlined"
            defaultValue={null}
          />
          <TextInput
            source="bank.name"
            label={translate('venues.bank_short', { _: 'Banka' })}
            fullWidth
            variant="outlined"
            defaultValue={null}
          />
          <TextInput
            source="bank.accountNo"
            label="IBAN"
            fullWidth
            variant="outlined"
            defaultValue={null}
          />
        </InputRow>
      </FormSection>

      <FormSection title="SEO">
        <InputRow>
          <TextInput
            source="google.keywords"
            label="Keywords"
            fullWidth
            variant="outlined"
            defaultValue={null}
          />
          <TextInput
            source="google.description"
            label={translate('venues.seo_desc_short', { _: 'SEO açıklama' })}
            fullWidth
            variant="outlined"
            defaultValue={null}
          />
        </InputRow>
      </FormSection>

      <FormSection title={translate('venues.managers', { _: 'Yöneticiler' })}>
        <SelectUserByEmailInput
          source="managers"
          multiple
          required={false}
          record={isEdit ? record : undefined}
        />
      </FormSection>
    </>
  );
}

export default VenueFormFields;
